import React from 'react';
import { useParams, Link } from 'react-router-dom';

const BlogPost = () => {
  const { slug } = useParams();

  // Blog post data - in a real app, this would come from a CMS or API
  const blogPosts = {
    'oral-cancer-signs': {
      title: 'Understanding Early Signs of Oral Cancer',
      author: 'Dr. Adesh Palyekar',
      readingTime: '4 min read',
      image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=400&fit=crop',
      imageAlt: 'Dental examination and oral health awareness',
      content: `
        <div class="mb-8">
          <img src="https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=400&fit=crop" alt="Dental examination and oral health awareness" class="w-full h-64 object-cover rounded-lg mb-6" />
          <p class="text-lg text-text-muted leading-relaxed">Oral cancer is a serious health concern that affects thousands of people worldwide. Early detection is crucial for successful treatment and improved outcomes. In this comprehensive guide, we'll explore the early signs of oral cancer, risk factors, and when to seek medical attention.</p>
        </div>
        
        <div class="bg-blue-50 border-l-4 border-accent-500 p-6 rounded-r-lg mb-8">
          <h3 class="font-montserrat text-xl font-bold text-primary-500 mb-3">Key Takeaways</h3>
          <ul class="space-y-2 text-text-muted">
            <li>• Early detection can significantly improve survival rates</li>
            <li>• Regular dental checkups are crucial for prevention</li>
            <li>• Know the warning signs and risk factors</li>
            <li>• Don't ignore persistent mouth sores or changes</li>
          </ul>
        </div>
        
        <h3 class="font-montserrat text-2xl font-bold text-primary-500 mb-4 mt-8">What is Oral Cancer?</h3>
        <p class="text-text-muted leading-relaxed mb-6">Oral cancer refers to cancer that develops in the mouth, including the lips, tongue, cheeks, floor of the mouth, hard and soft palate, sinuses, and pharynx. It can be life-threatening if not diagnosed and treated early.</p>
        
        <h3 class="font-montserrat text-2xl font-bold text-primary-500 mb-4">Early Warning Signs</h3>
        <p class="text-text-muted leading-relaxed mb-4">Being aware of these early signs can make a significant difference in treatment outcomes:</p>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div class="bg-white p-4 rounded-lg border border-primary-100">
            <h4 class="font-montserrat font-bold text-primary-500 mb-2">Physical Symptoms</h4>
            <ul class="space-y-1 text-sm text-text-muted">
              <li>• Persistent mouth sores (2+ weeks)</li>
              <li>• White or red patches in mouth</li>
              <li>• Unexplained bleeding</li>
              <li>• Difficulty chewing/swallowing</li>
            </ul>
          </div>
          <div class="bg-white p-4 rounded-lg border border-primary-100">
            <h4 class="font-montserrat font-bold text-primary-500 mb-2">Other Warning Signs</h4>
            <ul class="space-y-1 text-sm text-text-muted">
              <li>• Persistent sore throat</li>
              <li>• Numbness in tongue/mouth</li>
              <li>• Jaw swelling or pain</li>
              <li>• Loose teeth without cause</li>
            </ul>
          </div>
        </div>
        
        <h3 class="font-montserrat text-2xl font-bold text-primary-500 mb-4">Risk Factors</h3>
        <p class="text-text-muted leading-relaxed mb-4">Several factors can increase your risk of developing oral cancer:</p>
        <div class="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg mb-8">
          <h4 class="font-montserrat font-bold text-red-700 mb-3">High Risk Factors</h4>
          <ul class="space-y-2 text-red-700">
            <li><strong>Tobacco use:</strong> Smoking cigarettes, cigars, pipes, or using chewing tobacco</li>
            <li><strong>Heavy alcohol consumption:</strong> Especially when combined with tobacco use</li>
            <li><strong>Human papillomavirus (HPV):</strong> Certain strains of HPV can cause oral cancer</li>
            <li><strong>Excessive sun exposure:</strong> Particularly for lip cancer</li>
            <li><strong>Poor oral hygiene:</strong> Chronic irritation from ill-fitting dentures</li>
            <li><strong>Age:</strong> Most cases occur in people over 40</li>
          </ul>
        </div>
        
        <h3 class="font-montserrat text-2xl font-bold text-primary-500 mb-4">When to See a Specialist</h3>
        <p class="text-text-muted leading-relaxed mb-6">If you experience any of the early signs mentioned above, especially if they persist for more than two weeks, it's crucial to consult with a specialist. Early detection and treatment can significantly improve outcomes and survival rates.</p>
        
        <div class="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg mb-8">
          <h4 class="font-montserrat font-bold text-green-700 mb-3">Prevention Tips</h4>
          <p class="text-green-700 mb-3">While not all cases are preventable, you can reduce your risk by:</p>
          <ul class="space-y-1 text-green-700">
            <li>• Avoiding tobacco products</li>
            <li>• Limiting alcohol consumption</li>
            <li>• Protecting your lips from sun exposure</li>
            <li>• Maintaining good oral hygiene</li>
            <li>• Getting regular dental checkups</li>
            <li>• Eating a healthy diet rich in fruits and vegetables</li>
          </ul>
        </div>
        
        <div class="bg-accent-50 border border-accent-200 rounded-lg p-6 mt-8">
          <h4 class="font-montserrat font-bold text-accent-700 mb-2">Important Reminder</h4>
          <p class="text-accent-700">Remember, early detection is key. If you notice any concerning symptoms, don't hesitate to seek professional medical advice. Your health and well-being are worth the extra attention.</p>
        </div>
      `
    },
    'lower-cancer-risk': {
      title: 'How to Lower Your Risk of Cancer: Lifestyle Tips',
      author: 'Dr. Adesh Palyekar',
      readingTime: '5 min read',
      image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=400&fit=crop',
      imageAlt: 'Healthy lifestyle and cancer prevention',
      content: `
        <div class="mb-8">
          <img src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=400&fit=crop" alt="Healthy lifestyle and cancer prevention" class="w-full h-64 object-cover rounded-lg mb-6" />
          <p class="text-lg text-text-muted leading-relaxed">While not all cancers are preventable, research shows that healthy lifestyle choices can significantly reduce your risk of developing many types of cancer. In this comprehensive guide, we'll explore evidence-based strategies for cancer prevention.</p>
        </div>
        
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          <div class="bg-blue-50 p-6 rounded-lg">
            <h3 class="font-montserrat text-xl font-bold text-primary-500 mb-4">Maintain a Healthy Weight</h3>
            <p class="text-text-muted leading-relaxed mb-4">Obesity is linked to an increased risk of several types of cancer, including breast, colon, and kidney cancer. Aim to maintain a healthy weight through a balanced diet and regular exercise.</p>
            <div class="bg-white p-4 rounded border border-blue-200">
              <h4 class="font-montserrat font-bold text-primary-500 mb-2">Tips for Healthy Weight</h4>
              <ul class="space-y-1 text-sm text-text-muted">
                <li>• Calculate your BMI and work toward a healthy range</li>
                <li>• Focus on sustainable changes rather than quick fixes</li>
                <li>• Combine diet and exercise for best results</li>
              </ul>
            </div>
          </div>
          
          <div class="bg-green-50 p-6 rounded-lg">
            <h3 class="font-montserrat text-xl font-bold text-primary-500 mb-4">Exercise Regularly</h3>
            <p class="text-text-muted leading-relaxed mb-4">Physical activity helps maintain a healthy weight and may reduce cancer risk through various mechanisms. Aim for at least 150 minutes of moderate-intensity exercise per week.</p>
            <div class="bg-white p-4 rounded border border-green-200">
              <h4 class="font-montserrat font-bold text-primary-500 mb-2">Exercise Guidelines</h4>
              <ul class="space-y-1 text-sm text-text-muted">
                <li>• 150 minutes moderate exercise weekly</li>
                <li>• Include strength training 2x per week</li>
                <li>• Stay active throughout the day</li>
                <li>• Find activities you enjoy</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div class="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg mb-8">
          <h3 class="font-montserrat text-xl font-bold text-primary-500 mb-4">Eat a Healthy Diet</h3>
          <p class="text-text-muted leading-relaxed mb-4">Focus on a diet rich in fruits, vegetables, whole grains, and lean proteins. Limit processed foods, red meat, and sugary beverages.</p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <h4 class="font-montserrat font-bold text-primary-500 mb-2">Foods to Include</h4>
              <ul class="space-y-1 text-sm text-text-muted">
                <li>• Colorful fruits and vegetables</li>
                <li>• Whole grains (brown rice, quinoa)</li>
                <li>• Lean proteins (fish, poultry)</li>
                <li>• Healthy fats (nuts, olive oil)</li>
              </ul>
            </div>
            <div>
              <h4 class="font-montserrat font-bold text-primary-500 mb-2">Foods to Limit</h4>
              <ul class="space-y-1 text-sm text-text-muted">
                <li>• Processed meats</li>
                <li>• Sugary beverages</li>
                <li>• Refined grains</li>
                <li>• Excessive red meat</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div class="bg-red-50 p-6 rounded-lg">
            <h3 class="font-montserrat text-xl font-bold text-primary-500 mb-4">Avoid Tobacco</h3>
            <p class="text-text-muted leading-relaxed">Tobacco use is the leading cause of preventable cancer deaths. If you smoke, quitting is the best thing you can do for your health.</p>
          </div>
          
          <div class="bg-orange-50 p-6 rounded-lg">
            <h3 class="font-montserrat text-xl font-bold text-primary-500 mb-4">Limit Alcohol</h3>
            <p class="text-text-muted leading-relaxed">If you choose to drink alcohol, do so in moderation. Women should limit to one drink per day, men to two drinks per day.</p>
          </div>
        </div>
        
        <div class="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-lg mb-8">
          <h3 class="font-montserrat text-xl font-bold text-primary-500 mb-4">Protect Your Skin</h3>
          <p class="text-text-muted leading-relaxed mb-4">Use sunscreen, wear protective clothing, and avoid excessive sun exposure to reduce your risk of skin cancer.</p>
          <ul class="space-y-2 text-text-muted">
            <li>• Use sunscreen with SPF 30 or higher</li>
            <li>• Seek shade during peak sun hours (10 AM - 4 PM)</li>
            <li>• Wear protective clothing and sunglasses</li>
            <li>• Avoid tanning beds</li>
          </ul>
        </div>
        
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 class="font-montserrat text-xl font-bold text-primary-500 mb-4">Get Regular Screenings</h3>
          <p class="text-text-muted leading-relaxed">Regular cancer screenings can detect cancer early when it's most treatable. Talk to your doctor about appropriate screening schedules.</p>
        </div>
      `
    },
    'cancer-surgery-expect': {
      title: 'What to Expect During Cancer Surgery',
      author: 'Dr. Adesh Palyekar',
      readingTime: '6 min read',
      image: 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=400&fit=crop',
      imageAlt: 'Cancer surgery preparation and medical care',
      content: `
        <div class="mb-8">
          <img src="https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=400&fit=crop" alt="Cancer surgery preparation and medical care" class="w-full h-64 object-cover rounded-lg mb-6" />
          <p class="text-lg text-text-muted leading-relaxed">Cancer surgery can be a daunting prospect, but understanding what to expect can help you feel more prepared and confident. This comprehensive guide will walk you through the entire process, from pre-surgery preparation to recovery.</p>
        </div>
        
        <div class="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8">
          <h3 class="font-montserrat text-xl font-bold text-primary-500 mb-3">Pre-Surgery Preparation</h3>
          <p class="text-text-muted leading-relaxed mb-4">Before your surgery, you'll undergo several preparations:</p>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <ul class="space-y-2 text-text-muted">
              <li>• Medical evaluation and tests</li>
              <li>• Discussion of surgical options and risks</li>
              <li>• Pre-operative instructions (diet, medications)</li>
              <li>• Arrangements for post-surgery care</li>
            </ul>
          </div>
        </div>
        
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <div class="bg-white p-6 rounded-lg border border-primary-100">
            <div class="text-3xl text-accent-500 mb-4 text-center">1</div>
            <h4 class="font-montserrat font-bold text-primary-500 mb-3 text-center">The Day of Surgery</h4>
            <ul class="space-y-2 text-sm text-text-muted">
              <li>• Arrive at the hospital at scheduled time</li>
              <li>• Complete final paperwork and consent forms</li>
              <li>• Meet with the surgical team</li>
              <li>• Receive anesthesia</li>
              <li>• Undergo the surgical procedure</li>
            </ul>
          </div>
          
          <div class="bg-white p-6 rounded-lg border border-primary-100">
            <div class="text-3xl text-accent-500 mb-4 text-center">2</div>
            <h4 class="font-montserrat font-bold text-primary-500 mb-3 text-center">During Surgery</h4>
            <p class="text-sm text-text-muted">You'll be under general anesthesia during the procedure, so you won't be aware of what's happening. The surgical team will monitor your vital signs throughout the procedure.</p>
          </div>
          
          <div class="bg-white p-6 rounded-lg border border-primary-100">
            <div class="text-3xl text-accent-500 mb-4 text-center">3</div>
            <h4 class="font-montserrat font-bold text-primary-500 mb-3 text-center">Post-Surgery Recovery</h4>
            <p class="text-sm text-text-muted">After surgery, you'll be taken to a recovery room where medical staff will monitor your condition. You may experience pain, fatigue, nausea, or emotional changes.</p>
          </div>
        </div>
        
        <div class="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg mb-8">
          <h3 class="font-montserrat text-xl font-bold text-primary-500 mb-4">What You Might Experience</h3>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 class="font-montserrat font-bold text-primary-500 mb-2">Physical Symptoms</h4>
              <ul class="space-y-1 text-sm text-text-muted">
                <li>• Pain or discomfort at surgical site</li>
                <li>• Fatigue and weakness</li>
                <li>• Nausea or loss of appetite</li>
                <li>• Swelling or bruising</li>
              </ul>
            </div>
            <div>
              <h4 class="font-montserrat font-bold text-primary-500 mb-2">Emotional Changes</h4>
              <ul class="space-y-1 text-sm text-text-muted">
                <li>• Anxiety or worry</li>
                <li>• Mood swings</li>
                <li>• Difficulty sleeping</li>
                <li>• Emotional ups and downs</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div class="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg mb-8">
          <h3 class="font-montserrat text-xl font-bold text-primary-500 mb-4">Recovery Timeline</h3>
          <p class="text-text-muted leading-relaxed mb-4">Recovery time varies depending on the type and extent of surgery. Your medical team will provide specific guidance for your situation.</p>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div class="bg-white p-4 rounded border border-green-200">
              <h5 class="font-montserrat font-bold text-primary-500 mb-2">First Week</h5>
              <p class="text-sm text-text-muted">Focus on pain control and basic mobility</p>
            </div>
            <div class="bg-white p-4 rounded border border-green-200">
              <h5 class="font-montserrat font-bold text-primary-500 mb-2">2-4 Weeks</h5>
              <p class="text-sm text-text-muted">Gradual return to normal activities</p>
            </div>
            <div class="bg-white p-4 rounded border border-green-200">
              <h5 class="font-montserrat font-bold text-primary-500 mb-2">1-3 Months</h5>
              <p class="text-sm text-text-muted">Full recovery for most procedures</p>
            </div>
          </div>
        </div>
        
        <div class="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg mb-8">
          <h3 class="font-montserrat text-xl font-bold text-primary-500 mb-4">Follow-up Care</h3>
          <p class="text-text-muted leading-relaxed">Regular follow-up appointments are crucial for monitoring your recovery and detecting any potential complications early.</p>
        </div>
        
        <div class="bg-accent-50 border border-accent-200 rounded-lg p-6">
          <h4 class="font-montserrat font-bold text-accent-700 mb-2">Important Reminder</h4>
          <p class="text-accent-700">Remember, every patient's experience is unique. Don't hesitate to ask questions and communicate openly with your medical team throughout the process.</p>
        </div>
      `
    }
  };

  const post = blogPosts[slug];

  if (!post) {
    return (
      <main>
        <section className="section bg-background">
          <div className="container">
            <div className="max-w-2xl mx-auto text-center">
              <h1 className="section-title">Blog Post Not Found</h1>
              <p className="text-lg text-text-muted mb-8">
                The blog post you're looking for doesn't exist.
              </p>
              <Link to="/blog" className="btn-primary">
                ← Back to Blog
              </Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main>
      <section className="section bg-background">
        <div className="container">
          <article className="max-w-4xl mx-auto">
            <div className="card p-8 lg:p-12">
              <header className="mb-8">
                <h1 className="font-display text-3xl lg:text-4xl font-bold text-primary-700 mb-4" itemProp="headline">
                  {post.title}
                </h1>
                
                <div className="flex items-center gap-4 text-text-muted mb-6">
                  <span className="font-accent font-semibold" itemProp="author">{post.author}</span>
                  <span>•</span>
                  <span>{post.readingTime}</span>
                </div>
              </header>

              <div 
                className="prose prose-lg max-w-none text-text-muted leading-relaxed" 
                itemProp="articleBody"
                dangerouslySetInnerHTML={{ __html: post.content }}
              />

              <footer className="mt-12 pt-8 border-t border-primary-100">
                <Link to="/blog" className="text-accent-500 font-accent font-semibold hover:text-accent-600 transition-colors duration-300 inline-flex items-center gap-2">
                  <i className="fas fa-arrow-left text-sm"></i>
                  Back to Blog
                </Link>
              </footer>
            </div>
          </article>
        </div>
      </section>
    </main>
  );
};

export default BlogPost; 